/**
 * Story Analysis Service
 * 
 * Service for triggering GPT-powered story analysis and retrieving
 * persona readiness status (RoleRanger, QuizCritter)
 */

import { logger } from '../utils/logger';
import { getApiUrl } from '../utils/apiUtils';

export interface PersonaReadiness {
  ready: boolean;
  opportunities_count: number;
  status: 'ready' | 'no_opportunities' | 'analyzing' | 'error';
}

export interface RoleplayOpportunity {
  character_name: string;
  story_position: number;
  situation: string;
  dialogue_sample: string;
  emotional_tone?: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface QuizOpportunity {
  story_position: number;
  question_text: string;
  options: string[];
  correct_answer: string;
  difficulty: 'easy' | 'medium' | 'hard';
  question_type: string;
}

export interface StoryAnalysisResult {
  success: boolean;
  story_id: string;
  story_title: string;
  analysis_cached: boolean;
  persona_readiness: {
    role_ranger: PersonaReadiness;
    quiz_critter: PersonaReadiness;
  };
  roleplay_opportunities: RoleplayOpportunity[];
  quiz_opportunities: QuizOpportunity[];
  timestamp: string;
}

class StoryAnalysisService {
  private baseUrl: string;
  private analysisCache: Map<string, StoryAnalysisResult> = new Map();

  constructor() {
    // Use getApiUrl to properly handle development vs production URLs
    this.baseUrl = getApiUrl('/stories');
  }

  /**
   * Analyze a story for persona interventions
   * 
   * @param storyId - ID of the story to analyze
   * @param forceRefresh - Force re-analysis even if cached
   * @param userAge - User's age for logging
   * @param conversationId - Conversation ID for logging
   * @returns Story analysis results with persona readiness
   */
  async analyzeStory(
    storyId: string,
    forceRefresh: boolean = false,
    userAge?: number,
    conversationId?: string
  ): Promise<StoryAnalysisResult | null> {
    try {
      // Check local cache first (unless force refresh)
      if (!forceRefresh && this.analysisCache.has(storyId)) {
        logger.logUIEvent('story_analysis_cache_hit', {
          story_id: storyId,
          cached: true
        }, userAge, conversationId);
        
        return this.analysisCache.get(storyId)!;
      }

      logger.logUIEvent('story_analysis_request', {
        story_id: storyId,
        force_refresh: forceRefresh
      }, userAge, conversationId);

      const response = await fetch(
        `${this.baseUrl}/${storyId}/analyze?force_refresh=${forceRefresh}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to analyze story');
      }

      const result: StoryAnalysisResult = await response.json();

      // Cache the result locally
      this.analysisCache.set(storyId, result);

      logger.logUIEvent('story_analysis_complete', {
        story_id: storyId,
        cached: result.analysis_cached,
        role_ranger_ready: result.persona_readiness.role_ranger.ready,
        quiz_critter_ready: result.persona_readiness.quiz_critter.ready,
        roleplay_count: result.roleplay_opportunities.length,
        quiz_count: result.quiz_opportunities.length
      }, userAge, conversationId);

      return result;

    } catch (error) {
      logger.logErrorEvent('story_analysis_error', error as Error, {
        story_id: storyId,
        force_refresh: forceRefresh
      }, userAge, conversationId);

      console.error('Story analysis failed:', error);
      return null;
    }
  }

  /**
   * Get cached analysis result if available
   * 
   * @param storyId - ID of the story
   * @returns Cached analysis result or null
   */
  getCachedAnalysis(storyId: string): StoryAnalysisResult | null {
    return this.analysisCache.get(storyId) || null;
  }

  /**
   * Clear analysis cache for a specific story or all stories
   * 
   * @param storyId - Optional story ID to clear (clears all if not provided)
   */
  clearCache(storyId?: string): void {
    if (storyId) {
      this.analysisCache.delete(storyId);
    } else {
      this.analysisCache.clear();
    }
  }

  /**
   * Check if a story has been analyzed
   * 
   * @param storyId - ID of the story
   * @returns True if story has been analyzed and cached
   */
  hasAnalysis(storyId: string): boolean {
    return this.analysisCache.has(storyId);
  }
}

// Export singleton instance
export const storyAnalysisService = new StoryAnalysisService();
